import { useState } from 'react'
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ReportPager, ReportSection } from '@/components/reports/ReportSection'
import { ReportStates, downloadCsv, tooltipStyle, typeLabel } from './reportShared'
import { useProcedureBacklogReport } from '@/hooks/useProcedureVolumeReport'
import { reportFileName, toCsv } from '@/lib/reportCsv'
import type { ReportFilterSelection } from '@/lib/reportFilters'
import type { ReportTimeFrame } from '@/lib/reportRange'

/** "19 Sep 2026" — the day this snapshot describes. */
function asAtLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}

/**
 * Report 1.5 — open procedure backlog and aging.
 *
 * ## A snapshot, and it says so
 *
 * This is not a window: it is what was still open at ONE instant, the end of
 * the chosen period. The subtitle names that day, because a reader who has
 * picked "last 90 days" and sees a single number needs to know it describes the
 * last day of those ninety rather than all of them. A run opened long before
 * the window and still running is exactly what this reports, which is why the
 * period cannot bound the start.
 *
 * ## Colour carries the age
 *
 * The bands run green to red. On a backlog the reader's question is "how bad
 * is the tail", and a chart where every bar is the same colour makes them read
 * the axis to find out. Empty bands are still drawn — a gap in the middle of an
 * aging profile is information.
 */
const BAND_COLORS: Record<string, string> = {
  '0-7': '#1baf7a',
  '8-14': '#7cb342',
  '15-30': '#eda100',
  '31-60': '#e8762c',
  '61+': '#d64545',
}

const PAGE_SIZE = 20

/** Days as something a person says out loud. */
function humanDays(days: number | null): string {
  if (days === null) return '—'
  if (days < 1) return 'today'
  return `${days}d`
}

export function ProcedureBacklogReport({
  frame,
  filters,
}: {
  frame: ReportTimeFrame
  filters: ReportFilterSelection
}) {
  const query = useProcedureBacklogReport(frame, filters)
  const [page, setPage] = useState(1)
  const state = ReportStates(query)
  if (state) return state
  const report = query.data
  if (!report) return null

  const bands = report.buckets.map((b) => ({
    name: b.label,
    key: b.key,
    count: b.count,
    label: b.count > 0 ? String(b.count) : '',
  }))

  const pageCount = Math.max(1, Math.ceil(report.rows.length / PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const start = (current - 1) * PAGE_SIZE
  const rows = report.rows.slice(start, start + PAGE_SIZE)

  const exportCsv = () => {
    downloadCsv(
      toCsv(
        ['Started', 'Age (days)', 'Procedure type', 'Template', 'Work order', 'Patient MRN'],
        report.rows.map((r) => [
          r.startedOn,
          r.ageDays,
          typeLabel(r.type),
          r.templateName,
          r.workOrderNumber,
          r.patientMrn,
        ]),
      ),
      reportFileName('procedures-backlog', report.window),
    )
  }

  return (
    <div className='flex flex-col gap-4 p-4'>
      <ReportSection title='Open backlog' subtitle={`As at ${asAtLabel(report.asOf)}`}>
        <div className='flex flex-col gap-3 p-4'>
          {/* Every rolling preset ends TODAY, so 7, 30 and 90 days all ask for
              the same snapshot and return the same number. That looks broken
              unless the report says what the period does here. A custom window
              ending in the past is what moves this figure. */}
          <p className='text-xs text-muted-foreground'>
            A snapshot: only the period's end date applies.
          </p>
          <div className='flex flex-wrap items-end gap-x-8 gap-y-3'>
          <div>
            <p className='text-5xl font-semibold tabular-nums text-foreground'>{report.total}</p>
            <p className='mt-1 text-sm text-muted-foreground'>
              {report.total === 0 ? 'Nothing open on this day' : 'procedures still open'}
            </p>
          </div>
          <dl className='flex flex-wrap gap-x-6 gap-y-2'>
            <Stat label='Median age' value={humanDays(report.medianAgeDays)} />
            <Stat label='Average age' value={humanDays(report.averageAgeDays)} />
            <Stat
              label='Oldest'
              value={humanDays(report.oldestDays)}
              color={report.oldestDays !== null && report.oldestDays > 60 ? BAND_COLORS['61+'] : undefined}
            />
            </dl>
          </div>
        </div>
      </ReportSection>

      {report.total > 0 && (
        <ReportSection title='Age profile'>
          <div className='p-4'>
            <ResponsiveContainer width='100%' height={200}>
              <BarChart data={bands} margin={{ left: 0, right: 8, top: 16, bottom: 4 }}>
                <XAxis dataKey='name' tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v as number, 'Open']} />
                <Bar dataKey='count' radius={[4, 4, 0, 0]} barSize={48}>
                  {bands.map((b) => (
                    <Cell key={b.key} fill={BAND_COLORS[b.key] ?? '#9ca3af'} />
                  ))}
                  <LabelList dataKey='label' position='top' style={{ fontSize: 11 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ReportSection>
      )}

      {report.byType.length > 0 && (
        <ReportSection title='By procedure type'>
          <div className='overflow-x-persistent'>
            <table className='w-full text-xs'>
              <thead className='border-b bg-muted/40 text-left text-muted-foreground'>
                <tr>
                  <th className='px-3 py-2 font-medium'>Type</th>
                  <th className='px-3 py-2 text-right font-medium'>Open</th>
                  <th className='px-3 py-2 text-right font-medium'>Oldest</th>
                </tr>
              </thead>
              <tbody>
                {report.byType.map((t) => (
                  <tr key={t.type} className='border-b last:border-0'>
                    <td className='px-3 py-1.5'>{typeLabel(t.type)}</td>
                    <td className='px-3 py-1.5 text-right tabular-nums'>{t.count}</td>
                    <td className='px-3 py-1.5 text-right tabular-nums'>{humanDays(t.oldestDays)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ReportSection>
      )}

      <ReportSection title='Oldest first' onExport={report.rows.length > 0 ? exportCsv : undefined}>
        {report.rows.length === 0 ? (
          <p className='px-4 py-6 text-xs text-muted-foreground'>Nothing open on this day.</p>
        ) : (
          <>
            <div className='overflow-x-persistent'>
              <table className='w-full text-xs'>
                <thead className='border-b bg-muted/40 text-left text-muted-foreground'>
                  <tr>
                    <th className='px-3 py-2 font-medium'>Started</th>
                    <th className='px-3 py-2 text-right font-medium'>Age</th>
                    <th className='px-3 py-2 font-medium'>Type</th>
                    <th className='px-3 py-2 font-medium'>Template</th>
                    <th className='px-3 py-2 font-medium'>Work order</th>
                    <th className='px-3 py-2 font-medium'>Patient</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className='border-b last:border-0'>
                      <td className='px-3 py-1.5 tabular-nums'>{r.startedOn}</td>
                      <td
                        className='px-3 py-1.5 text-right font-medium tabular-nums'
                        style={{ color: r.ageDays > 60 ? BAND_COLORS['61+'] : undefined }}
                      >
                        {humanDays(r.ageDays)}
                      </td>
                      <td className='px-3 py-1.5'>{typeLabel(r.type)}</td>
                      <td className='px-3 py-1.5'>{r.templateName}</td>
                      <td className='px-3 py-1.5'>{r.workOrderNumber ?? '—'}</td>
                      <td className='px-3 py-1.5'>{r.patientMrn ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ReportPager
              page={current}
              pageCount={pageCount}
              from={start + 1}
              to={start + rows.length}
              total={report.rows.length}
              onPage={setPage}
            />
          </>
        )}
      </ReportSection>
    </div>
  )
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <dt className='text-xs text-muted-foreground'>{label}</dt>
      <dd className='text-xl font-semibold tabular-nums' style={{ color }}>
        {value}
      </dd>
    </div>
  )
}
